import { useEffect } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import parseGeoraster from "georaster";
import GeoRasterLayer from "georaster-layer-for-leaflet";
import geoblaze from "geoblaze";
import "proj4-fully-loaded";

type Props = {
  url: string;
  name: string;
  group: string;
  defaultOn?: boolean;
  opacity?: number;
  domain?: [number, number];
  /** 'bar' keeps your TAS legend; 'rows' uses the 6-class (or N-class) row legend */
  legendMode?: "bar" | "rows";
  /** number of classes for 'rows' legend */
  classes?: number;
  /** optional number formatting (decimals) for legend labels */
  decimals?: number;
};

export default function RasterGeoTiffOverlay({
  url,
  name,
  group,
  defaultOn = false,
  opacity = 0.85,
  domain,
  legendMode = "bar", // <-- TAS defaults to bar
  classes = 6, // <-- hailstorms/hurricanes use 6 classes
  decimals = 2,
}: Props) {
  const map = useMap();

  useEffect(() => {
    const added: L.Layer[] = [];
    let rasterLayer: L.Layer | undefined;

    // avoid double-mount during dev Fast Refresh
    const regKey = `raster:${group}:${name}:${url}`;
    const reg: Set<string> =
      (map as any)._rasterReg || ((map as any)._rasterReg = new Set());
    if (reg.has(regKey)) return;
    reg.add(regKey);

    // helpers
    const positionLegend = () => {
      const el = legend.getContainer();
      if (!el) return;
      const barEl = document.querySelector<HTMLElement>(
        ".search-bar-container"
      );
      const h = barEl?.getBoundingClientRect().height ?? 0;
      el.style.setProperty("margin-bottom", `${h + 30}px`);
    };

    const hex = (h: string) => {
      const m = /^#?([0-9a-f]{6})$/i.exec(h)!;
      const n = parseInt(m[1], 16);
      return [(n >> 16) & 255, (n >> 8) & 255, n & 255] as [
        number,
        number,
        number
      ];
    };
    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const lerpRGB = (
      c1: [number, number, number],
      c2: [number, number, number],
      t: number
    ) => {
      const r = Math.round(lerp(c1[0], c2[0], t));
      const g = Math.round(lerp(c1[1], c2[1], t));
      const b = Math.round(lerp(c1[2], c2[2], t));
      return `rgba(${r},${g},${b},1)`;
    };

    // same multi-stop ramp you already use
    const stops = [
      [0.0, "#ffffff"],
      [0.2, "#ffc78e"],
      [0.4, "#ffff00"],
      [0.6, "#00ffff"],
      [0.8, "#0000ff"],
      [1.0, "#7b00ff"],
    ] as const;
    const stopRGB = stops.map(([p, c]) => [p, hex(c as string)] as const);

    const makeRamp =
      (min: number, max: number) => (v: number | null | undefined) => {
        if (v == null || Number.isNaN(v)) return "rgba(0,0,0,0)";
        const span = max - min || 1;

        const t = Math.max(0, Math.min(1, (v - min) / span));
        for (let i = 1; i < stopRGB.length; i++) {
          const [p0, c0] = stopRGB[i - 1];
          const [p1, c1] = stopRGB[i];
          if (t <= p1 || i === stopRGB.length - 1) {
            const tt = (t - p0) / Math.max(1e-6, p1 - p0);
            return lerpRGB(c0 as any, c1 as any, Math.max(0, Math.min(1, tt)));
          }
        }
        return "rgba(0,0,0,0)";
      };

    // legend shell
    const legend = L.control({ position: "bottomleft" });
    legend.onAdd = () => {
      const div = L.DomUtil.create("div", "raster-legend");
      // Title always present
      div.innerHTML =
        legendMode === "bar"
          ? `
          <div class="rl-title">${name}</div>
          <div class="rl-bar"></div>
          <div class="rl-minmax">
            <span class="rl-min"></span>
            <span class="rl-max"></span>
          </div>`
          : `
          <div class="rl-title">${name}</div>
          <div class="rl-rows"></div>`;
      return div;
    };

    // renderer is defined after we know min/max
    let renderLegend: (() => void) | null = null;

    (async () => {
      try {
        const ab = await fetch(url).then((r) => {
          if (!r.ok) throw new Error(`HTTP ${r.status} for ${url}`);
          return r.arrayBuffer();
        });
        const georaster = await parseGeoraster(ab);

        const min = domain?.[0] ?? georaster.mins?.[0] ?? 0;
        const max = domain?.[1] ?? georaster.maxs?.[0] ?? 1;
        const colorOf = makeRamp(min, max);

        // pane under markers
        const paneName = "pane-rasters";
        if (!map.getPane(paneName)) {
          const p = map.createPane(paneName);
          p.style.zIndex = "350";
        }

        // raster
        rasterLayer = new (GeoRasterLayer as any)({
          georaster,
          pane: paneName,
          opacity,
          resolution: 256,
          pixelValuesToColorFn: (vals: number[]) => colorOf(vals?.[0]),
        });

        // register in grouped layer control
        const ctrl = (map as any)._groupedCtrl;
        if (ctrl && typeof ctrl.addOverlay === "function") {
          ctrl.addOverlay(rasterLayer, name, group);
        } else {
          const existing = Object.values((map as any)._controls || {}).find(
            (c: any) => typeof c.addOverlay === "function" && c._layers
          ) as any;
          if (existing) existing.addOverlay(rasterLayer, name, group);
          else console.warn("[Raster] grouped layer control not found.");
        }

        if (defaultOn) rasterLayer!.addTo(map);
        added.push(rasterLayer!);

        // tooltip (unchanged) This will change the info displayed on the hover
        const tip = L.tooltip({
          direction: "top",
          offset: [0, -8],
          className: "raster-tooltip",
          opacity: 0.95,
        });
        let tipOn = false;

        let v;
        const showTip = (ll: L.LatLng, val: any) => {
          if (
            name === "TAS Crossing Year (CMIP6 SSP245)" ||
            name === "TAS Crossing Year (CMIP6 SSP585)"
          ) {
            v = Array.isArray(val) ? val[0] : val;
          } else {
            v = Array.isArray(val) ? Math.floor((val[0] / 255) * 100) : val;
          }

          const content = v == null || Number.isNaN(v) ? "No data" : String(v);
          tip.setContent(content).setLatLng(ll);
          if (!tipOn) {
            tip.addTo(map);
            tipOn = true;
          }
        };
        const hideTip = () => {
          if (tipOn) {
            map.removeLayer(tip);
            tipOn = false;
          }
        };

        let last = 0;
        const onMove = (e: L.LeafletMouseEvent) => {
          const now = Date.now();
          if (now - last < 75) return;
          last = now;
          if (!map.hasLayer(rasterLayer!)) return;
          const { lat, lng } = e.latlng;
          const out = geoblaze.identify(georaster, [lng, lat]);
          if (out && typeof (out as any).then === "function") {
            (out as Promise<any>)
              .then((res) => showTip(e.latlng, res))
              .catch(() => {});
          } else {
            showTip(e.latlng, out);
          }
        };

        map.on("mousemove", onMove);
        map.on("zoomstart", hideTip);
        map.on("mouseout", hideTip);
        map.on("overlayremove", (ev: any) => {
          if (ev.layer === rasterLayer) hideTip();
        });

        // -------- legend rendering (mode-aware) --------
        const fmt = (v: number) => v.toFixed(decimals);

        if (legendMode === "bar") {
          // your existing bar + min/max, but fed by each layer's min/max
          renderLegend = () => {
            const el = legend.getContainer();
            if (!el) return;
            const minEl = el.querySelector<HTMLSpanElement>(".rl-min");
            const maxEl = el.querySelector<HTMLSpanElement>(".rl-max");
            const barEl = el.querySelector<HTMLDivElement>(".rl-bar");
            // if (minEl) minEl.textContent = fmt(min);
            if (minEl) minEl.textContent = "Low";
            if (maxEl) maxEl.textContent = "High";

            // optional: draw a CSS gradient bar using the same ramp
            if (barEl) {
              const gradStops = Array.from({ length: 16 }, (_, i) => {
                const t = i / 15;
                const v = min + t * (max - min);
                return `${colorOf(v)} ${Math.round(t * 100)}%`;
              }).join(", ");
              barEl.style.height = "10px";
              barEl.style.border = "1px solid #aaa";
              barEl.style.borderRadius = "3px";
              barEl.style.background = `linear-gradient(90deg, ${gradStops})`;
            }
          };
        } else {
          // 'rows' mode with N classes (default 6)
          renderLegend = () => {
            const el = legend.getContainer();
            if (!el) return;

            // build breaks
            const n = Math.max(1, Math.floor(classes));
            const breaks = Array.from(
              { length: n + 1 },
              (_, i) => min + (i * (max - min)) / n
            );
            const rowsEl =
              el.querySelector<HTMLDivElement>(".rl-rows") ||
              (() => {
                el.innerHTML = `<div class="rl-title">${name}</div><div class="rl-rows"></div>`;
                return el.querySelector(".rl-rows") as HTMLDivElement;
              })();

            // sample color in the middle of each bin for smoother look
            const rowHTML = Array.from({ length: n }, (_, i) => {
              const a = breaks[i];
              const b = breaks[i + 1] ?? max;
              const mid = (a + b) / 2;
              const c = colorOf(mid);
              return `<div style="display:flex;align-items:center;gap:8px;margin:2px 0;">
                <span style="display:inline-block;width:18px;height:12px;border:1px solid #aaa;background:${c};border-radius:2px"></span>
                <span style="font-size:12px;">${fmt(a)}–${fmt(b)}</span>
              </div>`;
            }).join("");

            rowsEl.innerHTML = rowHTML;
          };
        }

        // legend toggle + (re)render
        const updateLegend = () => {
          const on = map.hasLayer(rasterLayer!);
          try {
            if (on) {
              legend.addTo(map);
              renderLegend && renderLegend();
              positionLegend();
            } else {
              legend.remove();
            }
          } catch {}
        };

        map.on("overlayadd", (e: any) => {
          if (e.layer === rasterLayer) updateLegend();
        });
        map.on("overlayremove", (e: any) => {
          if (e.layer === rasterLayer) updateLegend();
        });

        if (defaultOn) {
          legend.addTo(map);
          renderLegend && renderLegend();
        }
        positionLegend();
        added.push(legend as any);

        const onResize = () => positionLegend();
        window.addEventListener("resize", onResize);
      } catch (e) {
        console.error("Raster load failed:", e);
      }
    })();

    // cleanup
    return () => {
      const c = (map as any)._groupedCtrl;
      if (c && rasterLayer && typeof c.removeLayer === "function") {
        try {
          c.removeLayer(rasterLayer);
        } catch {}
      }
      // NOTE: if you attach window listeners above, remove them here too
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    map,
    url,
    name,
    group,
    defaultOn,
    opacity,
    domain,
    legendMode,
    classes,
    decimals,
  ]);

  return null;
}
